import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { FaUserFriends, FaInfoCircle, FaPenNib, FaLock } from "react-icons/fa";

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function RegisterPage3({
  formData = {},
  initialData = null,
  onSubmit, // This is used to go to the next step (handleNext from RegisterForm)
  onPrevious,
  onDataChange,
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  // Add state for user role
  const [userRole, setUserRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = () => {
      const userData = localStorage.getItem("user");

      if (!userData) {
        setUserRole("user");
        setIsAdmin(false);
        return;
      }

      try {
        const parsedData = JSON.parse(userData);
        const role = (parsedData.role || "user").toLowerCase();

        setUserRole(role);
        setIsAdmin(role === "admin");

      } catch (error) {
        console.error("Error parsing user data:", error);
        setUserRole("user");
        setIsAdmin(false);
      }
    };

    checkUserRole();
  }, []);

  // Auto-fill form data from initialData when component mounts
  useEffect(() => {
    if (initialData && onDataChange) {
      const autoFillData = {
        // Map from API response to form fields
        disability:
          initialData.statement?.is_disability_discrimination ||
          initialData.form_data?.disability ||
          "",
        disabilityDetails:
          initialData.statement?.disability_discrimination_detail ||
          initialData.form_data?.disabilityDetails ||
          "",
        criminalConviction:
          initialData.statement?.is_criminal_conviction ||
          initialData.form_data?.criminalConviction ||
          "",
        criminalConvictionDetails:
          initialData.statement?.criminal_conviction_detail ||
          initialData.form_data?.criminalConvictionDetails ||
          "",
        personalStatement:
          initialData.statement?.personal_statement ||
          initialData.form_data?.personalStatement ||
          "",
      };

      // Only update fields that are empty in current formData to avoid overwriting user changes
      const fieldsToUpdate = {};
      Object.keys(autoFillData).forEach((key) => {
        if (!formData[key] && autoFillData[key]) {
          fieldsToUpdate[key] = autoFillData[key];
        }
      });

      if (Object.keys(fieldsToUpdate).length > 0) {
        onDataChange(fieldsToUpdate);
      }
    }
  }, [initialData, formData]); // ✅ FIXED: Removed onDataChange to prevent infinite loop

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("register_3", JSON.stringify(formData));
    }
  }, [formData]);

  // Modified handleChange to prevent changes for non-admin users
  const handleChange = (e) => {
    // If not admin, prevent changes
    if (!isAdmin) {
      return;
    }

    const { name, value } = e.target;

    // Update form data through parent
    onDataChange({ [name]: value });

    // Clear details when "No" is selected
    if (name === "disability" && value === "No") {
      onDataChange({ disabilityDetails: "" });
    }
    if (name === "criminalConviction" && value === "No") {
      onDataChange({ criminalConvictionDetails: "" });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    setIsProcessing(true);

    // Call onSubmit to move to the next step
    if (onSubmit) {
      onSubmit(formData);
    }

    setTimeout(() => {
      setIsProcessing(false);
    }, 500);
  };

  const isFormValid = () => {
    return (
      formData.disability &&
      formData.criminalConviction &&
      formData.personalStatement?.trim() &&
      formData.personalStatement.length <= 500 &&
      (formData.disability !== "Yes" || formData.disabilityDetails?.trim()) &&
      (formData.criminalConviction !== "Yes" ||
        formData.criminalConvictionDetails?.trim())
    );
  };

  // Admin Lock Indicator Component
  const AdminLockIndicator = ({ isAdmin }) => {
    if (isAdmin) return null;
    
    return (
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center text-yellow-700">
          <FaLock className="mr-2" />
          <span className="text-sm font-medium">
            View-only mode: Only administrators can edit these fields
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 sm:p-4 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-white">
                Confirmation & Submission
                {!isAdmin && (
                  <span className="ml-3 text-sm bg-yellow-500 text-white px-2 py-1 rounded">
                    View Only
                  </span>
                )}
              </h1>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[75vh] p-6 md:p-8 space-y-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Add admin lock indicator */}
            <AdminLockIndicator isAdmin={isAdmin} />
            
            {/* Disability Section */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaUserFriends className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Disability</h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-4"
              >
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Do you have a disability as defined under the Disability
                    Discrimination Act? *
                  </label>
                  <div className="flex gap-8">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className={`inline-flex items-center space-x-2 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      >
                        <input
                          type="radio"
                          name="disability"
                          value={opt}
                          onChange={handleChange}
                          checked={formData.disability === opt}
                          disabled={!isAdmin}
                          className={`h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                          required
                        />
                        <span className={`text-gray-700 ${!isAdmin ? 'text-gray-500' : ''}`}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {formData.disability === "Yes" && (
                    <motion.div
                      key="disabilityDetails"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className="block mb-2 font-semibold text-gray-700">
                        Please provide details about your disability *
                      </label>
                      <textarea
                        name="disabilityDetails"
                        placeholder="Please provide details about your disability..."
                        value={formData.disabilityDetails || ""}
                        onChange={handleChange}
                        rows="3"
                        disabled={!isAdmin}
                        required={formData.disability === "Yes"}
                        className={`w-full border border-slate-300 bg-white px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${
                          !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                        }`}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Criminal Conviction Section */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaInfoCircle className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Criminal Conviction
                </h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-4"
              >
                <div>
                  <label className="block mb-2 font-semibold text-gray-700">
                    Do you have any criminal conviction(s)? *
                  </label>
                  <div className="flex gap-8">
                    {["Yes", "No"].map((opt) => (
                      <label
                        key={opt}
                        className={`inline-flex items-center space-x-2 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      >
                        <input
                          type="radio"
                          name="criminalConviction"
                          value={opt}
                          onChange={handleChange}
                          checked={formData.criminalConviction === opt}
                          disabled={!isAdmin}
                          className={`h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
                          required
                        />
                        <span className={`text-gray-700 ${!isAdmin ? 'text-gray-500' : ''}`}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {formData.criminalConviction === "Yes" && (
                    <motion.div
                      key="criminalConvictionDetails"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <label className="block mb-2 font-semibold text-gray-700">
                        Please provide details about your criminal conviction *
                      </label>
                      <textarea
                        name="criminalConvictionDetails"
                        placeholder="Please provide details about your criminal conviction..."
                        value={formData.criminalConvictionDetails || ""}
                        onChange={handleChange}
                        rows="3"
                        disabled={!isAdmin}
                        required={formData.criminalConviction === "Yes"}
                        className={`w-full border border-slate-300 bg-white px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition ${
                          !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                        }`}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Personal Statement Section */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaPenNib className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Personal Statement
                </h3>
              </div>
              <motion.div
                variants={fieldVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                <label className="block mb-2 font-semibold text-gray-700">
                  Personal Statement *
                </label>
                <textarea
                  name="personalStatement"
                  placeholder="Please provide your statement..."
                  value={formData.personalStatement || ""}
                  onChange={handleChange}
                  maxLength="500"
                  rows="8"
                  disabled={!isAdmin}
                  required
                  className={`w-full border border-slate-300 bg-white px-4 py-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                    !isAdmin ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                  }`}
                />
                <div className="text-sm text-slate-500">
                  Statements should be approximately 500 words and will be
                  scrutinised for plagiarism.
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">* Required fields</span>
                  <span
                    className={`${
                      (formData.personalStatement || "").length > 450
                        ? "text-orange-600"
                        : (formData.personalStatement || "").length > 500
                        ? "text-red-600"
                        : "text-slate-500"
                    }`}
                  >
                    {(formData.personalStatement || "").length} / 500 characters
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <button
                type="button"
                onClick={onPrevious}
                disabled={isProcessing}
                className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <motion.button
                whileHover={{ scale: isFormValid() ? 1.02 : 1 }}
                whileTap={{ scale: isFormValid() ? 0.98 : 1 }}
                type="submit"
                disabled={isProcessing || !isFormValid()}
                className={`px-6 py-3 rounded-lg font-medium transition flex-1 text-white shadow-md ${
                  isFormValid()
                    ? "bg-gradient-to-r from-blue-600 to-cyan-600 hover:opacity-95 hover:shadow-lg"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                } disabled:opacity-70`}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  "Continue to Final Step"
                )}
              </motion.button>
            </div>
          </form>
        </div>
    </div>
  );
}